// src/Services/videochatAtoms.js
//
// Estado global de videochat (Jotai). Centraliza la emisión por socket + las
// llamadas HTTP para que VideochatHost y ChatScreen disparen exactamente la
// misma lógica. Ver docs/videochat-camera-off-behavior.md para el resto de la
// UI de la llamada en sí (VideochatView) — esto es solo el handshake previo.
import { atom } from 'jotai'
import axios from 'axios'
import config from '../Config'
import { authUserAtom, dataUserAtom } from './store/atoms'
import { getVideochatSocket } from './videochatSocket'

// Conversaciones del usuario — la puebla VideochatHost al iniciar sesión.
// Sirve para (a) saber a qué rooms unirse y (b) resolver nombre/avatar del
// otro usuario para la UI de la llamada. Forma: [{ chat: { id }, partner: { nickname, avatar_url } }]
export const conversationsAtom = atom([])

// Solicitud entrante sonando: { chatId, fromName, fromId, requesterSocketId }
export const incomingVideochatAtom = atom(null)
// Llamada en curso: { chatId, partnerId, partnerName, partnerAvatarUrl, isCaller }
// isCaller decide quién crea el WebRTC offer (ver VideochatView) — antes se
// usaba el rol de cuenta (`role === 'model'`), pero eso asumía que toda
// llamada era client↔model. Desde que se permite videollamar entre cualquier
// par de roles, dos peers sin ningún `model` en la llamada quedaban con
// isModel=false en ambos lados y nadie mandaba el offer (se quedaba en
// "Conectando..." para siempre). isCaller en cambio sale del propio handshake
// request/accept: quien inició la solicitud es siempre el caller, sin
// importar el rol de ninguno de los dos.
export const activeVideochatAtom = atom(null)
// La llamada en curso se puede minimizar a una burbuja y seguir usando la app.
// Vive acá (no en VideochatView) porque el Host decide el tamaño de su envoltorio.
export const videochatMinimizedAtom = atom(false)
// Solicitud saliente propia: { chatId, status: 'requesting' | 'rejected', message? }
// ('rejected' también cubre ocupado/bloqueo, con `message` para mostrar)
export const outgoingVideochatAtom = atom(null)

// El token viaja explícito en cada llamada, igual que turn-credentials en
// VideochatView, en vez de depender de axios.defaults: ese header global lo
// pisan decenas de pantallas y en una pestaña que cambió de cuenta sin recargar
// puede quedar con el token de otra sesión. Así pasó el 2026-09-25: request,
// cancel y respond de latina69 llegaban a nombre de una cuenta ajena al chat,
// el backend respondía 403 "No autorizado" y no salía la push de la llamada.
const authHeaders = (get) => ({ headers: { Authorization: `Bearer ${get(authUserAtom)?.token}` } })

const findPartner = (conversations, chatId) =>
  conversations.find(c => String(c?.chat?.id) === String(chatId))?.partner ?? null

// Arranca la llamada activa a partir de un chatId (se llama al recibir
// 'videochat-accepted' del lado que hizo la solicitud).
export const startActiveVideochatAtom = atom(null, (get, set, chatId) => {
  const partner = findPartner(get(conversationsAtom), chatId)
  set(activeVideochatAtom, {
    chatId: String(chatId),
    partnerId: partner?.id ?? null,
    partnerName: partner?.nickname ?? null,
    partnerAvatarUrl: partner?.avatar_url ?? null,
    isCaller: true,
  })
})

// Texto que se muestra cuando la llamada no llega a sonar. `glare` no tiene
// texto a propósito: el otro ya me está llamando en este chat, se quita mi
// llamada sin aviso y suena la suya.
const DECLINE_MESSAGES = {
  busy: 'Está en otra videollamada',
  caller_busy: 'Ya estás en una videollamada',
  blocked: 'No puedes llamar a este usuario',
}

// La llamada saliente no llegó a sonar: ocupado (409 de request o evento
// `videochat-busy`) o bloqueo/modo fantasma (403). Pueden llegar los dos para
// la misma llamada y cuenta el primero: solo actúa si sigue en 'requesting'.
// NO emite videochat-cancel ni llama a cancel-request: la llamada nunca sonó, y
// cancelarla liberaría el estado de la otra llamada, la que sí existe.
export const callDeclinedAtom = atom(null, (get, set, { chatId, reason }) => {
  const outgoing = get(outgoingVideochatAtom)
  if (outgoing?.status !== 'requesting' || outgoing.chatId !== String(chatId)) return
  if (reason === 'glare') {
    set(outgoingVideochatAtom, null)
    return
  }
  // 'rejected' hace que ChatScreen oculte el botón mientras dura el aviso.
  set(outgoingVideochatAtom, {
    chatId: outgoing.chatId,
    status: 'rejected',
    message: DECLINE_MESSAGES[reason] ?? DECLINE_MESSAGES.busy,
  })
})

export const requestVideochatAtom = atom(null, async (get, set, chatId) => {
  const dataUser = get(dataUserAtom)
  const id = String(chatId)
  set(outgoingVideochatAtom, { chatId: id, status: 'requesting' })
  getVideochatSocket()?.emit('videochat-request', {
    chatId,
    fromName: dataUser?.nickname || dataUser?.name || 'Usuario',
    fromId: dataUser?.id,
  })
  try {
    await axios.post(`${config.REACT_APP_BASE_URL}/api/v1/chat/videochat/${chatId}/request`, null, authHeaders(get))
  } catch (err) {
    // 409 (ocupado) y 403 (bloqueo o modo fantasma) cortan la llamada; un fallo
    // de red no: el aviso por el relay ya salió, así que se sigue sonando.
    const status = err?.response?.status
    if (status === 409) set(callDeclinedAtom, { chatId, reason: err.response.data?.reason ?? 'busy' })
    else if (status === 403) set(callDeclinedAtom, { chatId, reason: 'blocked' })
    else console.warn('[VC] request HTTP (ignorable):', err)
  }
})

export const cancelVideochatAtom = atom(null, async (get, set) => {
  const outgoing = get(outgoingVideochatAtom)
  if (outgoing?.status !== 'requesting') return
  const { chatId } = outgoing
  set(outgoingVideochatAtom, null)
  getVideochatSocket()?.emit('videochat-cancel', { chatId })
  try {
    await axios.post(`${config.REACT_APP_BASE_URL}/api/v1/chat/videochat/${chatId}/cancel-request`, null, authHeaders(get))
  } catch (err) {
    console.warn('[VC] cancel HTTP (ignorable):', err)
  }
})

export const acceptVideochatAtom = atom(null, async (get, set) => {
  const incoming = get(incomingVideochatAtom)
  if (!incoming) return
  const { chatId } = incoming
  set(incomingVideochatAtom, null)
  getVideochatSocket()?.emit('videochat-accept', { chatId })
  const partner = findPartner(get(conversationsAtom), chatId)
  set(activeVideochatAtom, {
    chatId: String(chatId),
    partnerId: partner?.id ?? incoming.fromId ?? null,
    partnerName: partner?.nickname ?? incoming.fromName ?? null,
    partnerAvatarUrl: partner?.avatar_url ?? null,
    isCaller: false,
  })
  try {
    await axios.post(`${config.REACT_APP_BASE_URL}/api/v1/chat/videochat/${chatId}/respond`, { response: 'accepted' }, authHeaders(get))
  } catch (err) {
    console.warn('[VC] accept HTTP (ignorable):', err)
  }
})

export const rejectVideochatAtom = atom(null, async (get, set) => {
  const incoming = get(incomingVideochatAtom)
  if (!incoming) return
  const { chatId } = incoming
  set(incomingVideochatAtom, null)
  getVideochatSocket()?.emit('videochat-reject', { chatId })
  try {
    await axios.post(`${config.REACT_APP_BASE_URL}/api/v1/chat/videochat/${chatId}/respond`, { response: 'rejected' }, authHeaders(get))
  } catch (err) {
    console.warn('[VC] reject HTTP (ignorable):', err)
  }
})
